import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { AppShell } from "@/components/AppShell";
import { Input } from "@/components/ui/input";
import { ReplaceMealDialog } from "@/components/plan/ReplaceMealDialog";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { BookOpen, Clock, Flame, Crown, Lock, Search, Repeat, Heart } from "lucide-react";
import { FavoriteButton } from "@/components/FavoriteButton";
import { Stars } from "@/components/Stars";
import { useFavorites } from "@/lib/favorites";

const CATS = ["breakfast", "lunch", "dinner", "snack"];
const DIETS = ["vegan", "vegetarian", "pescatarian"];
const TAG_BADGES = ["vegan", "vegetarian", "pescatarian", "gluten_free"];
const ALLERGENS = ["gluten", "dairy", "nuts", "eggs", "fish", "shellfish", "soy"];
const TIMES = [15, 30, 45];
const TIERS = ["free", "plus", "premium"];

function Chip({ on, onClick, children, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${on ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border hover:bg-secondary"}`}>
      {children}
    </button>
  );
}

export default function Recipes() {
  const { t, tv } = useLang();
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [cat, setCat] = useState(null);
  const [diets, setDiets] = useState([]);
  const [noAllergens, setNoAllergens] = useState([]);
  const [maxTime, setMaxTime] = useState(null);
  const [tier, setTier] = useState(null);
  const [onlyFav, setOnlyFav] = useState(false);
  const [replace, setReplace] = useState(null);
  const { isFavorite, paid } = useFavorites();

  const { data } = useQuery({ queryKey: ["recipes"], queryFn: async () => (await api.get("/recipes")).data });
  const toggle = (arr, set, v) => set(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

  const list = useMemo(() => (data?.recipes || []).filter((r) =>
    (!cat || r.category === cat) &&
    diets.every((d) => r.tags.includes(d)) &&
    !noAllergens.some((a) => r.allergens.includes(a)) &&
    (!maxTime || r.total_time <= maxTime) &&
    (!tier || (r.tier || "free") === tier) &&
    (!onlyFav || isFavorite(r.id)) &&
    (!q || tv(r.name).toLowerCase().includes(q.toLowerCase()))
  ), [data, cat, diets, noAllergens, maxTime, tier, onlyFav, isFavorite, q, tv]);

  const openReplace = (r) => {
    if (r.locked) { toast(`${t("tier_required")} ${t(`tier_${r.tier}`)}`, { action: { label: t("pricing"), onClick: () => nav("/pricing") } }); return; }
    setReplace(r);
  };

  return (
    <AppShell>
      <div className="flex items-center gap-3 chenia-fade-up">
        <span className="grid place-items-center h-11 w-11 rounded-2xl bg-primary text-primary-foreground"><BookOpen className="h-5 w-5" /></span>
        <div>
          <h1 className="font-serif text-3xl sm:text-4xl font-semibold tracking-tight">{t("recipes_title")}</h1>
          <p className="text-muted-foreground text-sm sm:text-base">{t("recipes_sub")}</p>
        </div>
      </div>

      <div className="mt-6 space-y-3" data-testid="recipes-filters">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("search_recipe")} className="rounded-xl pl-9" data-testid="recipes-search" />
        </div>
        <div className="flex flex-wrap gap-1.5">
          <Chip on={!cat} onClick={() => setCat(null)} testid="filter-cat-all">{t("all")}</Chip>
          {CATS.map((c) => <Chip key={c} on={cat === c} onClick={() => setCat(cat === c ? null : c)} testid={`filter-cat-${c}`}>{t(c)}</Chip>)}
        </div>
        <div className="flex flex-wrap gap-1.5">
          {DIETS.map((d) => <Chip key={d} on={diets.includes(d)} onClick={() => toggle(diets, setDiets, d)} testid={`filter-diet-${d}`}>{t(d)}</Chip>)}
          <span className="w-px bg-border mx-1" />
          {TIMES.map((m) => <Chip key={m} on={maxTime === m} onClick={() => setMaxTime(maxTime === m ? null : m)} testid={`filter-time-${m}`}>≤ {m} {t("min")}</Chip>)}
        </div>
        <div className="flex flex-wrap gap-1.5">
          {TIERS.map((x) => <Chip key={x} on={tier === x} onClick={() => setTier(tier === x ? null : x)} testid={`filter-tier-${x}`}>{t(`tier_${x}`)}</Chip>)}
          <span className="w-px bg-border mx-1" />
          <Chip on={onlyFav} onClick={() => setOnlyFav(!onlyFav)} testid="filter-favorites">
            <span className="inline-flex items-center gap-1"><Heart className={`h-3 w-3 ${onlyFav ? "fill-current" : ""}`} /> {t("my_favorites")} {!paid && <Lock className="h-3 w-3 opacity-70" />}</span>
          </Chip>
        </div>
        <div className="flex flex-wrap gap-1.5 items-center">
          <span className="text-xs text-muted-foreground mr-1">{t("allergies")}:</span>
          {ALLERGENS.map((a) => <Chip key={a} on={noAllergens.includes(a)} onClick={() => toggle(noAllergens, setNoAllergens, a)} testid={`filter-allergen-${a}`}>✕ {t(`al_${a}`)}</Chip>)}
        </div>
      </div>

      <p className="mt-5 text-sm text-muted-foreground" data-testid="recipes-count">{list.length} {t("results")}</p>
      {list.length === 0 && <p className="mt-2 text-muted-foreground" data-testid="recipes-empty">{t("no_recipes")}</p>}

      <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="recipes-grid">
        {list.map((r) => (
          <div key={r.id} className="chenia-card overflow-hidden group" data-testid={`recipe-card-${r.id}`}>
            <Link to={`/recipe/${r.id}`} className="block relative h-40">
              <img src={r.image} alt="" style={{ objectPosition: r.focal || "50% 50%" }} className={`h-full w-full object-cover group-hover:scale-[1.02] transition-transform duration-500 ${r.locked ? "grayscale-[30%]" : ""}`} />
              <span className="absolute top-3 left-3 rounded-full bg-background/90 backdrop-blur px-3 py-1 text-xs font-semibold uppercase tracking-wide">{t(r.category)}</span>
              {r.tier && r.tier !== "free" && (
                <span className={`absolute top-3 right-3 rounded-full px-2.5 py-1 text-xs font-bold tracking-wide inline-flex items-center gap-1 ${r.tier === "premium" ? "bg-primary text-primary-foreground" : "bg-chenia-sage text-foreground"}`} data-testid={`recipe-premium-${r.id}`}>
                  {r.locked ? <Lock className="h-3 w-3" /> : <Crown className="h-3 w-3" />} {t(`tier_${r.tier}`)}
                </span>
              )}
              <FavoriteButton recipeId={r.id} size="sm" className="absolute bottom-3 right-3" />
            </Link>
            <div className="p-4">
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-semibold leading-snug">{tv(r.name)}</h3>
                {r.note?.rating && <Stars value={r.note.rating} testid={`recipe-rating-${r.id}`} />}
              </div>
              <div className="mt-2 flex items-center gap-4 text-sm text-muted-foreground">
                <span className="inline-flex items-center gap-1"><Flame className="h-4 w-4" /> {r.nutrition ? `${r.nutrition.calories} ${t("kcal")}` : "—"}</span>
                <span className="inline-flex items-center gap-1"><Clock className="h-4 w-4" /> {r.total_time} {t("min")}</span>
              </div>
              <div className="mt-2 flex flex-wrap gap-1">
                {r.tags.filter((x) => TAG_BADGES.includes(x)).map((x) => <span key={x} className="text-[11px] rounded-full bg-chenia-sage/30 px-2 py-0.5">{t(x)}</span>)}
              </div>
              <button onClick={() => openReplace(r)} data-testid={`recipe-replace-${r.id}`}
                className="mt-3 w-full inline-flex items-center justify-center gap-1.5 rounded-full border border-border py-2 text-sm font-medium hover:bg-secondary transition-colors">
                <Repeat className="h-4 w-4" /> {t("replace_meal_with")} {r.locked && <Lock className="h-3 w-3 text-muted-foreground" />}
              </button>
            </div>
          </div>
        ))}
      </div>

      <ReplaceMealDialog open={!!replace} onClose={() => setReplace(null)} recipe={replace} onDone={() => nav("/meal-plan")} />
    </AppShell>
  );
}
